import type {
  RegisterToggleElements,
  RegisterToggleMode,
} from './toggle-types';

interface JQueryLike {
  trigger(eventName: string): JQueryLike;
}

interface JQueryStatic {
  (target: Element): JQueryLike;
}

type RegisterToggleWindow = Window & {
  jQuery?: JQueryStatic;
  PIFSTER_TURNSTILE?: {
    readonly siteKey: string;
  } | null;
};

const ADMIN_POST_ACTION_URL = '/wp-admin/admin-post.php';
const MAGIC_LINK_ACTION = 'pifster_login_gate_magic_link_request';
const TURNSTILE_RESPONSE_SELECTOR = 'input[name="cf-turnstile-response"]';
const TURNSTILE_TOKEN_SELECTOR = 'input[name="turnstile_token"]';

function getWindow(): RegisterToggleWindow {
  return window as RegisterToggleWindow;
}

function isTurnstileEnabled(): boolean {
  const turnstile = getWindow().PIFSTER_TURNSTILE;
  return Boolean(turnstile?.siteKey);
}

function dispatchModeEvents(field: HTMLInputElement): void {
  const events = ['input', 'change'] as const;
  for (const eventName of events) {
    try {
      field.dispatchEvent(new Event(eventName, { bubbles: true }));
    } catch {
      // No-op: some browsers or test harnesses may not support synthetic events here.
    }
  }

  const jQueryInstance = getWindow().jQuery;
  if (!jQueryInstance) {
    return;
  }

  try {
    jQueryInstance(field).trigger('input').trigger('change');
  } catch {
    // No-op: jQuery fallback is best effort only.
  }
}

function removeIfConnected(node: Element | null): void {
  if (node && node.isConnected) {
    node.remove();
  }
}

function ensureHiddenInput(
  form: HTMLFormElement,
  existing: HTMLInputElement | null,
  selector: string,
  name: string,
  value: string,
): HTMLInputElement {
  const normalizedValue = value;
  const ownerDocument = form.ownerDocument;
  if (!ownerDocument) {
    throw new Error('[register-toggle] Register form is missing its owner document.');
  }

  let input = existing;
  if (!input || !input.isConnected) {
    input = form.querySelector<HTMLInputElement>(selector);
  }

  if (!input) {
    input = ownerDocument.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = normalizedValue;
  return input;
}

function clearCustomTurnstileArtifacts(elements: RegisterToggleElements): void {
  if (elements.turnstileSource !== 'custom') {
    return;
  }

  removeIfConnected(elements.turnstileContainerEl);
  removeIfConnected(elements.turnstileTokenInputEl);
  elements.turnstileContainerEl = null;
  elements.turnstileTokenInputEl = null;
  elements.turnstileSource = null;
}

function setTurnstileReferenceState(
  elements: RegisterToggleElements,
  container: HTMLElement | null,
  tokenInput: HTMLInputElement | null,
  source: 'fluent' | 'custom',
): void {
  elements.turnstileContainerEl = container;
  elements.turnstileTokenInputEl = tokenInput;
  elements.turnstileSource = source;
}

/**
 * Sync the Fluent Forms conditional field value to the current register toggle mode.
 */
export function setFluentFormMode(elements: RegisterToggleElements, mode: RegisterToggleMode): void {
  if (!elements.modeFieldEl) {
    return;
  }

  elements.modeFieldEl.value = mode;
  dispatchModeEvents(elements.modeFieldEl);
}

/**
 * Set up the login-only form fields and Turnstile nodes required for the magic-link flow.
 */
export function setupMagicLinkFields(elements: RegisterToggleElements, redirectUrl: string, nonce: string): void {
  elements.form.action = ADMIN_POST_ACTION_URL;

  elements.hiddenActionInputEl = ensureHiddenInput(
    elements.form,
    elements.hiddenActionInputEl,
    'input[name="action"]',
    'action',
    MAGIC_LINK_ACTION,
  );

  elements.hiddenNonceInputEl = ensureHiddenInput(
    elements.form,
    elements.hiddenNonceInputEl,
    'input[name="_pifster_nonce"]',
    '_pifster_nonce',
    nonce,
  );

  elements.hiddenRedirectInputEl = ensureHiddenInput(
    elements.form,
    elements.hiddenRedirectInputEl,
    'input[name="redirect_to"]',
    'redirect_to',
    redirectUrl,
  );

  const fluentTurnstileInput = elements.form.querySelector<HTMLInputElement>(TURNSTILE_RESPONSE_SELECTOR);
  const fluentTurnstileContainer = elements.form.querySelector<HTMLElement>('.cf-turnstile');

  if (fluentTurnstileInput || fluentTurnstileContainer) {
    clearCustomTurnstileArtifacts(elements);
    elements.fluentTurnstileContainerEl = fluentTurnstileContainer;
    elements.fluentTurnstileInputEl = fluentTurnstileInput;
    setTurnstileReferenceState(elements, fluentTurnstileContainer, fluentTurnstileInput, 'fluent');
    return;
  }

  elements.fluentTurnstileContainerEl = null;
  elements.fluentTurnstileInputEl = null;

  if (!isTurnstileEnabled()) {
    clearCustomTurnstileArtifacts(elements);
    return;
  }

  if (elements.turnstileSource === 'custom' && elements.turnstileContainerEl?.isConnected && elements.turnstileTokenInputEl?.isConnected) {
    elements.turnstileTokenInputEl.value = elements.turnstileTokenInputEl.value || '';
    return;
  }

  clearCustomTurnstileArtifacts(elements);

  const ownerDocument = elements.form.ownerDocument;
  if (!ownerDocument) {
    throw new Error('[register-toggle] Register form is missing its owner document.');
  }

  const customContainer = ownerDocument.createElement('div');
  customContainer.className = 'pifster-turnstile';
  customContainer.setAttribute('data-turnstile-context', 'magic-link');
  customContainer.style.cssText = 'margin:12px 0;';

  if (elements.submitButtonEl?.parentElement) {
    elements.submitButtonEl.parentElement.insertBefore(customContainer, elements.submitButtonEl);
  } else {
    elements.form.appendChild(customContainer);
  }

  let tokenInput = elements.form.querySelector<HTMLInputElement>(TURNSTILE_TOKEN_SELECTOR);
  if (!tokenInput) {
    tokenInput = ownerDocument.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'turnstile_token';
    tokenInput.value = '';
    elements.form.appendChild(tokenInput);
  }

  setTurnstileReferenceState(elements, customContainer, tokenInput, 'custom');
}

/**
 * Restore the registration form back to its original state.
 */
export function restoreRegisterFields(elements: RegisterToggleElements): void {
  elements.form.action = elements.originalFormAction;

  removeIfConnected(elements.hiddenActionInputEl);
  removeIfConnected(elements.hiddenNonceInputEl);
  removeIfConnected(elements.hiddenRedirectInputEl);

  elements.hiddenActionInputEl = null;
  elements.hiddenNonceInputEl = null;
  elements.hiddenRedirectInputEl = null;

  if (elements.turnstileSource === 'custom') {
    removeIfConnected(elements.turnstileContainerEl);
    removeIfConnected(elements.turnstileTokenInputEl);
  }

  elements.turnstileContainerEl = null;
  elements.turnstileTokenInputEl = null;
  elements.fluentTurnstileContainerEl = null;
  elements.fluentTurnstileInputEl = null;
  elements.turnstileSource = null;
}
